import { artistSupabase } from "@/infrastructure/supabase/client";

const PROMPT_SUGGESTIONS_SLOT = "mobile.genart.prompt_suggestions";
const REVALIDATE_AFTER_MS = 15 * 60 * 1000;

export interface DesignerPromptSuggestion {
	readonly id: string;
	readonly caption: string;
	readonly prompt: string;
	readonly imageURL: string;
}

interface CachedSuggestions {
	readonly expiresAt: number;
	readonly items: readonly DesignerPromptSuggestion[];
}

const cache = new Map<string, CachedSuggestions>();
const inFlight = new Map<string, Promise<readonly DesignerPromptSuggestion[]>>();

function objectValue(value: unknown): Record<string, unknown> | null {
	return typeof value === "object" && value !== null
		? (value as Record<string, unknown>)
		: null;
}

function parseJSON(value: unknown): unknown {
	if (typeof value !== "string") return value;
	try {
		return JSON.parse(value) as unknown;
	} catch {
		return null;
	}
}

function validImageURL(value: unknown): string | null {
	if (typeof value !== "string") return null;
	try {
		const url = new URL(value);
		return url.protocol === "https:" ? url.href : null;
	} catch {
		return null;
	}
}

function payloadItems(value: unknown): readonly Record<string, unknown>[] {
	const payload = objectValue(parseJSON(value));
	const items = payload?.items;
	if (!Array.isArray(items)) return [];
	return items.flatMap((item) => {
		const object = objectValue(item);
		return object === null ? [] : [object];
	});
}

function localeCandidates(locale: string): readonly string[] {
	const language = locale.split("-")[0] ?? locale;
	return locale === language ? [locale, "en"] : [locale, language, "en"];
}

function localizedPayload(value: unknown, locale: string): unknown {
	const payloads = objectValue(parseJSON(value));
	if (payloads === null) return null;
	for (const candidate of localeCandidates(locale)) {
		if (candidate in payloads) return payloads[candidate];
	}
	return null;
}

function normalizedSuggestions(
	basePayload: unknown,
	localizedValue: unknown,
): readonly DesignerPromptSuggestion[] {
	const baseItems = payloadItems(basePayload);
	const localizedByID = new Map(
		payloadItems(localizedValue).flatMap((item) =>
			typeof item.id === "string" ? [[item.id, item] as const] : [],
		),
	);

	return baseItems.flatMap((base) => {
		if (typeof base.id !== "string") return [];
		const localized = localizedByID.get(base.id);
		const caption = localized?.caption ?? base.caption;
		const prompt = localized?.prompt ?? base.prompt;
		const imageURL = validImageURL(localized?.image_url ?? base.image_url);
		if (
			typeof caption !== "string" ||
			caption.trim().length === 0 ||
			typeof prompt !== "string" ||
			prompt.trim().length === 0 ||
			imageURL === null
		) return [];
		return [{
			id: base.id,
			caption: caption.trim(),
			prompt: prompt.trim(),
			imageURL,
		}];
	});
}

export async function loadDesignerPromptSuggestions(
	locale: string,
): Promise<readonly DesignerPromptSuggestion[]> {
	const cached = cache.get(locale);
	if (cached !== undefined && cached.expiresAt > Date.now()) return cached.items;
	const pending = inFlight.get(locale);
	if (pending !== undefined) return pending;

	const request = Promise.resolve(
		artistSupabase()
			.from("designer_content_public")
			.select("payload_text,localized_payloads_text")
			.eq("slot_key", PROMPT_SUGGESTIONS_SLOT)
			.maybeSingle(),
	)
		.then(({ data, error }) => {
			if (error) throw error;
			const items = data === null
				? []
				: normalizedSuggestions(
						data.payload_text,
						localizedPayload(data.localized_payloads_text, locale),
					);
			cache.set(locale, { expiresAt: Date.now() + REVALIDATE_AFTER_MS, items });
			return items;
		})
		.finally(() => inFlight.delete(locale));
	inFlight.set(locale, request);
	return request;
}
